import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { salesApi } from "../../lib/sales-api";
import type {
  SalesCallGroupType,
  SalesScore,
  SalesGroup,
} from "../../lib/sales-types";
import { Button } from "../../components/ui/Button";
import { Panel } from "../../components/ui/Panel";
import { Select } from "../../components/ui/Select";
import { cn } from "../../components/ui/cn";
import {
  moved,
  useReorder,
  type ReorderHandlers,
} from "../../components/ui/useReorder";
import { SavedFlash, inputClass } from "./shared";

/**
 * Everything about sales calls a manager can change without a deploy.
 *
 * The brief asks for this in v1 and the reason is worth keeping in view: this
 * desk's vocabulary changes with the business. A new brand arrives, an event
 * type stops mattering, a score turns out to mean two different things. None of
 * those should be a ticket for an engineer, so nothing in the module reads a
 * hardcoded group, score or threshold — the closing buttons, the worklist
 * order and the red badges all read these sections.
 *
 * There is no priority tier here any more. The desk's *groups* are its tiers:
 * "High priority" was a group and "High" was a priority, and nobody could say
 * what a High-priority call inside the Low-priority group meant.
 *
 * Nothing here deletes. Retiring hides an option from the pickers and leaves it
 * readable on every call that already carries it, because a closed call whose
 * score vanished is a call with no recorded outcome.
 */
export function SalesSettingsSections({ isAdmin }: { isAdmin: boolean }) {
  const config = useQuery({
    queryKey: queryKeys.salesConfig,
    queryFn: salesApi.config,
  });

  return (
    <>
      <OverdueRules isAdmin={isAdmin} config={config.data} />
      <Groups isAdmin={isAdmin} groups={config.data?.groups} />
      <Scores isAdmin={isAdmin} scores={config.data?.scores} />
    </>
  );
}

/** Invalidate the one key every screen in the module reads. */
function useConfigMutation<TArgs>(
  fn: (args: TArgs) => Promise<unknown>,
  onDone?: () => void,
) {
  const client = useQueryClient();
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const mutation = useMutation({
    mutationFn: fn,
    onSuccess: () => {
      /* The whole module's root, not just the config: changing a threshold
         re-decides which calls are overdue, so every list and count on screen
         is now wrong until it refetches. */
      void client.invalidateQueries({ queryKey: queryKeys.salesRoot });
      setSavedAt(Date.now());
      onDone?.();
    },
  });

  return { mutation, savedAt };
}

// ── Overdue rules ───────────────────────────────────────────────────────────

function OverdueRules({
  isAdmin,
  config,
}: {
  isAdmin: boolean;
  config: { rules: OverdueRulesShape } | undefined;
}) {
  const { mutation, savedAt } = useConfigMutation(salesApi.setRules);
  const rules = config?.rules;

  return (
    <Panel collapsible title="When a call needs attention">
      {/*
        The paragraph that used to sit here is gone -- it explained what the
        three sentences below already say. What it *also* carried, and what
        cannot be guessed from the controls, now hangs off them as tooltips: a
        promised callback suppresses all of this, and a due date on one call
        beats it.
      */}
      <div className="space-y-2">
        <Rule
          before="Nobody has rung it"
          after="days after it was raised"
          value={toDays(rules?.noAttemptHours ?? null)}
          disabled={!isAdmin}
          onSave={(value) =>
            value !== null &&
            mutation.mutate({ noAttemptHours: toHours(value) ?? 0 })
          }
        />

        <Rule
          before="It has been"
          after="days since the last attempt"
          value={rules?.staleDays}
          disabled={!isAdmin}
          onSave={(value) =>
            value !== null && mutation.mutate({ staleDays: value })
          }
        />

        {/*
          The third rule, and the only one that ships off. On a long-cycle
          account "we have been at this a while" is normal, so a desk has to
          ask for it before it becomes a red badge — which is why blank is a
          real value here and the placeholder says what blank means.
        */}
        <Rule
          before="It has been open"
          after="days, however often it has been rung"
          value={rules?.daysSinceRaised}
          placeholder="off"
          disabled={!isAdmin}
          onSave={(value) => mutation.mutate({ daysSinceRaised: value })}
        />
      </div>

      <div className="mt-3 flex items-center gap-3">
        <SavedFlash pending={mutation.isPending} at={savedAt} />
      </div>

      {/*
        The two rules people are most surprised by, said once and in the place
        they would go looking for them.
      */}
      {mutation.isError ? <Problem error={mutation.error} /> : null}
    </Panel>
  );
}

/**
 * The first threshold is asked for and shown in days, and stored in hours.
 *
 * Days is how the desk talks about it -- "chase it the next day" -- and hours
 * on screen made people do arithmetic to write a number they already knew. The
 * column stays in hours because it costs nothing and keeps sub-day chasing
 * possible if a desk ever wants it, without another migration.
 */
function toDays(hours: number | null): number | null {
  return hours === null ? null : Math.round(hours / 24);
}

function toHours(days: number | null): number | null {
  return days === null ? null : days * 24;
}

interface OverdueRulesShape {
  noAttemptHours: number;
  staleDays: number;
  daysSinceRaised: number | null;
}

/**
 * One threshold, written as the sentence it is.
 *
 * "Nobody has rung it [24] hours after it was raised" rather than a label, a
 * box and a unit in three separate columns. These are read once every few
 * months by somebody deciding whether the desk is chasing hard enough, and a
 * sentence is what they are actually trying to write.
 */
function Rule({
  before,
  after,
  value,
  placeholder,
  disabled,
  onSave,
}: {
  before: string;
  after: string;
  value: number | null | undefined;
  placeholder?: string;
  disabled?: boolean;
  onSave: (value: number | null) => void;
}) {
  return (
    <label
      className="flex flex-wrap items-center gap-2 text-sm text-content"
      title="A promised callback suppresses this until the time passes, and a due date on one call beats it outright."
    >
      {before}
      <input
        type="number"
        min={0}
        placeholder={placeholder}
        /* Keyed on the stored value so the box re-seeds when the query lands;
           uncontrolled otherwise, so typing is not fought by a refetch. */
        key={String(value)}
        defaultValue={value ?? ""}
        disabled={disabled}
        onBlur={(event) => {
          const raw = event.target.value.trim();
          const next = raw === "" ? null : Number(raw);
          if (next !== null && !Number.isFinite(next)) return;
          if (next !== (value ?? null)) onSave(next);
        }}
        className={cn(inputClass, "w-20")}
      />
      {after}
    </label>
  );
}

// ── Groups ──────────────────────────────────────────────────────────────────

/*
 * The three kinds this desk has. "Other" is still a valid stored value -- a
 * group filed under it keeps reading correctly -- but it is not offered, because
 * a fourth option meaning "I could not decide" is where everything ends up.
 */
const GROUP_TYPES: { value: SalesCallGroupType; label: string }[] = [
  { value: "brand", label: "Brand" },
  { value: "event", label: "Event" },
  { value: "priority", label: "Priority" },
];

function Groups({
  isAdmin,
  groups,
}: {
  isAdmin: boolean;
  groups: SalesGroup[] | undefined;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState<SalesCallGroupType>("brand");

  const { mutation: update, savedAt } = useConfigMutation(
    ({ id, patch }: { id: number; patch: Partial<SalesGroup> }) =>
      salesApi.updateGroup(id, patch),
  );

  const { mutation: create } = useConfigMutation<void>(
    () => salesApi.createGroup({ name: name.trim(), type }),
    () => {
      setName("");
      setAdding(false);
    },
  );

  const rows = groups ?? [];

  /*
   * Saves only the rows whose index actually changed. Dropping one row two
   * places up moves three of them; renumbering the whole list would be five
   * PATCHes to record two facts.
   */
  const reorder = useReorder((from, to) => {
    moved(rows, from, to).forEach((group, index) => {
      if (group.position !== index) {
        update.mutate({ id: group.id, patch: { position: index } });
      }
    });
  }, { enabled: isAdmin });

  return (
    <Panel collapsible title="Call groups">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-faint">
              {isAdmin ? <th className="w-14 px-1 py-1.5 font-medium" /> : null}
              <th className="px-2 py-1.5 font-medium">Call group</th>
              <th className="px-2 py-1.5 font-medium">Type</th>
              <th className="px-2 py-1.5 font-medium">With no attempt</th>
              <th className="px-2 py-1.5 font-medium">From last attempt</th>
              {isAdmin ? <th className="w-20 px-2 py-1.5 font-medium" /> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((group, index) => (
              <tr
                key={group.id}
                {...reorder.row(index)}
                className={cn(
                  "border-b border-line/60",
                  !group.isActive && "opacity-60",
                  reorder.dragging === index && "opacity-40",
                  reorder.over === index &&
                    reorder.dragging !== index &&
                    "border-t-2 border-t-brand-500",
                )}
              >
                {isAdmin ? (
                  <td className="px-1 py-1">
                    <Grip
                      label={`Drag ${group.name} to reorder`}
                      {...reorder.handle(index)}
                    />
                  </td>
                ) : null}

                <td className="px-2 py-1">
                  <input
                    defaultValue={group.name}
                    disabled={!isAdmin}
                    onBlur={(event) =>
                      event.target.value.trim() !== group.name &&
                      event.target.value.trim() !== "" &&
                      update.mutate({
                        id: group.id,
                        patch: { name: event.target.value.trim() },
                      })
                    }
                    className={cn(inputClass, "w-40")}
                  />
                </td>

                <td className="px-2 py-1">
                  <Select
                    value={group.type}
                    onChange={(value) =>
                      update.mutate({
                        id: group.id,
                        patch: { type: value as SalesCallGroupType },
                      })
                    }
                    label="Group type"
                    options={GROUP_TYPES}
                  />
                </td>

                <td className="px-2 py-1">
                  <Threshold
                    unit="days"
                    value={toDays(group.overdueNoAttemptHours)}
                    disabled={!isAdmin}
                    onSave={(value) =>
                      update.mutate({
                        id: group.id,
                        patch: { overdueNoAttemptHours: toHours(value) },
                      })
                    }
                  />
                </td>

                <td className="px-2 py-1">
                  <Threshold
                    unit="days"
                    value={group.overdueStaleDays}
                    disabled={!isAdmin}
                    onSave={(value) =>
                      update.mutate({
                        id: group.id,
                        patch: { overdueStaleDays: value },
                      })
                    }
                  />
                </td>

                {isAdmin ? (
                  <td className="px-2 py-1 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={group.isActive ? "Archive" : "Restore"}
                      title={
                        group.isActive
                          ? "Archive — hides it from the pickers, keeps it on calls already filed under it"
                          : "Restore"
                      }
                      onClick={() =>
                        update.mutate({
                          id: group.id,
                          patch: { isActive: !group.isActive },
                        })
                      }
                    >
                      {group.isActive ? "✕" : "Restore"}
                    </Button>
                  </td>
                ) : null}
              </tr>
            ))}

            {/* The new row, filled out the same way as the ones above it. Its
                rules are left to the desk defaults; set them once it exists. */}
            {isAdmin && adding ? (
              <tr className="border-b border-line/60">
                <td className="px-1 py-1" />
                <td className="px-2 py-1">
                  <input
                    autoFocus
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="Shelly Pro"
                    className={cn(inputClass, "w-40")}
                  />
                </td>
                <td className="px-2 py-1">
                  <Select
                    value={type}
                    onChange={(value) => setType(value as SalesCallGroupType)}
                    label="Type"
                    options={GROUP_TYPES}
                  />
                </td>
                <td className="px-2 py-1 text-xs text-faint" colSpan={2}>
                  Desk rules until you set its own
                </td>
                <td className="px-2 py-1 text-right">
                  <Button
                    size="sm"
                    disabled={!name.trim() || create.isPending}
                    onClick={() => create.mutate()}
                  >
                    Add
                  </Button>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {isAdmin ? (
        <div className="mt-2 flex items-center gap-3">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setAdding((open) => !open)}
            aria-label="Add a call group"
          >
            {adding ? "Cancel" : "+ Add a group"}
          </Button>
          <SavedFlash pending={update.isPending} at={savedAt} />
        </div>
      ) : null}

      {create.isError ? <Problem error={create.error} /> : null}
      {update.isError ? <Problem error={update.error} /> : null}
    </Panel>
  );
}

/**
 * One cell of the rules table.
 *
 * Blank means "use the desk rule", which is why the placeholder says so rather
 * than showing the inherited number — a number in the box would look like a
 * setting somebody had made, and clearing it would then read as deleting it.
 */
function Threshold({
  unit,
  value,
  disabled,
  onSave,
}: {
  unit: string;
  value: number | null;
  disabled?: boolean;
  onSave: (value: number | null) => void;
}) {
  return (
    <span className="flex items-center gap-1.5 text-xs text-muted">
      <input
        type="number"
        min={0}
        placeholder="desk rule"
        /* Keyed on the stored value so the box re-seeds when the query lands;
           uncontrolled otherwise, so typing is not fought by a refetch. */
        key={String(value)}
        defaultValue={value ?? ""}
        disabled={disabled}
        onBlur={(event) => {
          const raw = event.target.value.trim();
          const next = raw === "" ? null : Number(raw);
          if (next !== null && !Number.isFinite(next)) return;
          if (next !== value) onSave(next);
        }}
        className={cn(inputClass, "w-24 text-xs")}
      />
      {unit}
    </span>
  );
}

// ── Scores ──────────────────────────────────────────────────────────────────

const TONES = [
  { value: "positive", label: "Good" },
  { value: "neutral", label: "Neither" },
  { value: "negative", label: "Bad" },
];

function Scores({
  isAdmin,
  scores,
}: {
  isAdmin: boolean;
  scores: SalesScore[] | undefined;
}) {
  const [label, setLabel] = useState("");
  const [tone, setTone] = useState("neutral");

  const { mutation: update, savedAt } = useConfigMutation(
    ({ id, patch }: { id: number; patch: Partial<SalesScore> }) =>
      salesApi.updateScore(id, patch),
  );

  const { mutation: create } = useConfigMutation<void>(
    () =>
      salesApi.createScore({
        label: label.trim(),
        tone,
        position: (scores ?? []).length,
      }),
    () => setLabel(""),
  );

  return (
    <Panel collapsible title="Scores">
      <Ordered
        rows={scores ?? []}
        isAdmin={isAdmin}
        label={(row) => row.label}
        extra={(row) => (
          <Select
            value={row.tone}
            onChange={(value) =>
              update.mutate({
                id: row.id,
                patch: { tone: value as SalesScore["tone"] },
              })
            }
            label="Reads as"
            options={TONES}
          />
        )}
        onRename={(row, next) =>
          update.mutate({ id: row.id, patch: { label: next } })
        }
        onMove={(row, position) =>
          update.mutate({ id: row.id, patch: { position } })
        }
        onToggle={(row) =>
          update.mutate({ id: row.id, patch: { isActive: !row.isActive } })
        }
      />

      {isAdmin ? (
        <div className="mt-4 flex items-center gap-2 border-t border-line pt-3">
          <input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Quoted, waiting"
            className={cn(inputClass, "w-44")}
          />
          <Select
            value={tone}
            onChange={setTone}
            label="Reads as"
            options={TONES}
          />
          <Button
            size="sm"
            disabled={!label.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Add score
          </Button>
          <SavedFlash pending={update.isPending} at={savedAt} />
        </div>
      ) : null}

      {create.isError ? <Problem error={create.error} /> : null}
      {update.isError ? <Problem error={update.error} /> : null}
    </Panel>
  );
}

/**
 * An ordered, renameable, retirable list.
 *
 * Shared by scores and tiers because they are the same widget, and two copies
 * would be two places for "retire" to start meaning "delete".
 *
 * Reordering is two arrows rather than drag-and-drop. These lists hold four or
 * five rows, arrows work on a phone and with a keyboard, and drag-and-drop is a
 * great deal of code to move "Medium" above "Low" twice a year.
 */
function Ordered<
  T extends { id: number; position: number; isActive: boolean },
>({
  rows,
  isAdmin,
  label,
  extra,
  onRename,
  onMove,
  onToggle,
}: {
  rows: T[];
  isAdmin: boolean;
  label: (row: T) => string;
  extra?: (row: T) => React.ReactNode;
  onRename: (row: T, next: string) => void;
  onMove: (row: T, position: number) => void;
  onToggle: (row: T) => void;
}) {
  const reorder = useReorder((from, to) => {
    moved(rows, from, to).forEach((entry, index) => {
      if (entry.position !== index) onMove(entry, index);
    });
  }, { enabled: isAdmin });

  return (
    <div className="space-y-1">
      {rows.map((row, index) => (
        <div
          key={row.id}
          {...reorder.row(index)}
          className={cn(
            "flex flex-wrap items-center gap-2 rounded-md border border-line px-2 py-1.5",
            !row.isActive && "opacity-60",
            reorder.dragging === index && "opacity-40",
            reorder.over === index &&
              reorder.dragging !== index &&
              "border-t-2 border-t-brand-500",
          )}
        >
          {isAdmin ? (
            <Grip
              label={`Drag ${label(row)} to reorder`}
              {...reorder.handle(index)}
            />
          ) : null}
          <input
            defaultValue={label(row)}
            disabled={!isAdmin}
            onBlur={(event) =>
              event.target.value.trim() !== label(row) &&
              event.target.value.trim() !== "" &&
              onRename(row, event.target.value.trim())
            }
            className={cn(inputClass, "w-44")}
          />

          {extra?.(row)}

          {isAdmin ? (
            <span className="ml-auto flex items-center gap-1">
              {/* A cross, not the word. It sits at the end of a row of short
                  controls, and "Retire" was the widest thing in the panel for
                  an action nobody takes twice a year. */}
              <Button
                size="sm"
                variant="ghost"
                aria-label={row.isActive ? "Retire" : "Restore"}
                title={
                  row.isActive
                    ? "Retire — hides it from the pickers, keeps it on calls that already carry it"
                    : "Restore"
                }
                onClick={() => onToggle(row)}
              >
                {row.isActive ? "✕" : "Restore"}
              </Button>
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * The drag handle.
 *
 * A visible grip rather than making the whole row draggable and hoping people
 * discover it: a row full of inputs that also drags is a row where selecting
 * text in a field starts a drag instead.
 */
function Grip({
  label,
  ...handle
}: { label: string } & ReturnType<ReorderHandlers["handle"]>) {
  return (
    <span
      {...handle}
      role="button"
      aria-label={label}
      title={label}
      className="cursor-grab select-none px-1 text-faint active:cursor-grabbing"
    >
      ⠿
    </span>
  );
}

function Problem({ error }: { error: unknown }) {
  return (
    <p className="mt-2 text-xs text-red-600">
      {error instanceof ApiError ? error.message : "That did not save."}
    </p>
  );
}
